import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { OpsRole, PaymentResponse } from '../../api/types';
import { usePaymentProof } from './usePaymentProof';
import { formatMoney, parsePaise } from './allocation';
import { OrderLabel } from './OrderLabel';
import { StatusChip } from './StatusChip';
import { canAllocateReceiptCredit } from './receiptCredit';
import { ApproveRejectBar } from './ApproveRejectBar';
import { formatIstDateTime } from './paymentDates';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';
import { DialogHelp } from '../../help/DialogHelp';

function label(value: string) {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (character: string) => character.toUpperCase());
}

type HistoryItem = {
  label: string;
  at: string;
  actorId: number | null;
  actorName?: string | null;
  reason?: string | null;
};

function paymentHistory(payment: PaymentResponse): HistoryItem[] {
  const history: HistoryItem[] = [
    {
      label: payment.claimedByUserId ? 'Claim submitted' : 'Created',
      at: payment.createdAt,
      actorId: payment.claimedByUserId ?? payment.createdByOpsUserId,
      actorName: payment.claimedByName ?? payment.createdByName,
    },
  ];
  if (payment.decidedAt)
    history.push({
      label:
        payment.status === 'REJECTED'
          ? 'Rejected'
          : payment.claimedByUserId
            ? 'Claim verified'
            : 'Approved',
      at: payment.decidedAt,
      actorId: payment.decidedByOpsUserId,
      actorName: payment.decidedByName,
      reason: payment.status === 'REJECTED' ? payment.statusReason : null,
    });
  if (payment.paidRecordedAt)
    history.push({
      label: 'Recorded paid',
      at: payment.paidRecordedAt,
      actorId: payment.paidRecordedByOpsUserId,
      actorName: payment.paidRecordedByName,
    });
  if (payment.voidedAt)
    history.push({
      label: 'Cancelled',
      at: payment.voidedAt,
      actorId: payment.voidedByOpsUserId,
      actorName: payment.voidedByName,
      reason: payment.statusReason,
    });
  return history;
}

function isImageProof(payment: PaymentResponse): boolean {
  return /\.(?:jpe?g|png)$/i.test(payment.proofFileName ?? payment.proofFileKey ?? '');
}

export function PaymentDrawer({
  payment,
  role,
  onClose,
  onApprove,
  onVerifyClaim,
  onReject,
  onMarkPaid,
  onAllocateCredit,
  onVoidPayment,
  onVoidAllocation,
  actionError = null,
  actionPending = false,
  readOnly = false,
}: {
  payment: PaymentResponse;
  role: OpsRole;
  onClose: () => void;
  onVerifyClaim?: (paymentId: number) => void;
  onApprove?: (paymentId: number) => void;
  onReject?: (paymentId: number) => void;
  onMarkPaid?: (paymentId: number) => void;
  onAllocateCredit?: (paymentId: number) => void;
  onVoidPayment: (paymentId: number) => void;
  onVoidAllocation: (allocationId: number) => void;
  actionError?: string | null;
  actionPending?: boolean;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const manager = role === 'OPS_MANAGER' && !readOnly;
  const history = useMemo(() => paymentHistory(payment), [payment]);
  const imageProof = isImageProof(payment);
  const proof = usePaymentProof(payment.id, Boolean(payment.proofFileKey), imageProof);

  return (
    <div className={styles.drawerScrim} onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Payment ${payment.id}`}
        className={styles.drawer}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.drawerHeader}>
          <h2>
            {t('payments.paymentDrawer.heading', { id: payment.id, direction: payment.direction })}
          </h2>
          <div className={styles.actions}>
            <DialogHelp context="payment-details" title={`Payment ${payment.id}`} />
            <Button variant="secondary" size="sm" aria-label={t('payments.paymentDrawer.closeLabel')} onClick={onClose}>
              ×
            </Button>
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.footer}>
            <span>
              <StatusChip displayStatus={payment.status} />
            </span>
            <strong>{formatMoney(parsePaise(payment.amount))}</strong>
          </div>
          <div className={styles.subtle}>
            {payment.method ?? 'Transfer'} · {payment.reference ?? 'No reference'}
          </div>
          {payment.direction === 'IN' && payment.status === 'PAID' && (
            <div className={styles.credit}>
              {t('payments.paymentDrawer.unallocatedCredit', { amount: formatMoney(parsePaise(payment.unallocatedCredit)) })}
            </div>
          )}
        </div>
        {!readOnly && payment.status === 'CLAIMED' && onVerifyClaim && (
          <Button
            variant="primary" size="sm"
            disabled={actionPending}
            onClick={() => onVerifyClaim(payment.id)}
          >
            {t('payments.paymentDrawer.verifyClaim')}
          </Button>
        )}
        {payment.paidAt && <p>{t('payments.paymentDrawer.paidAt', { date: formatIstDateTime(payment.paidAt) })}</p>}
        {payment.direction === 'IN' && payment.status === 'VOID' && (
          <p>{t('payments.paymentDrawer.cancelledReceipt')}</p>
        )}
        {onAllocateCredit && canAllocateReceiptCredit(payment, readOnly) && (
          <Button
            variant="primary" size="sm"
            disabled={actionPending}
            onClick={() => onAllocateCredit(payment.id)}
          >
            {t('payments.paymentDrawer.allocateCredit')}
          </Button>
        )}
        {payment.status === 'REJECTED' && payment.statusReason && (
          <div role="alert" className={styles.error}>
            <strong>{payment.claimedByUserId ? 'Claim rejected:' : 'Payout rejected:'}</strong>{' '}
            {payment.statusReason}
          </div>
        )}
        <div className={styles.card}>
          <div className={styles.sectionTitle}>
            <h3>{t('payments.paymentDrawer.auditNote')}</h3>
          </div>
          {payment.note ?? 'No audit note recorded.'}
        </div>
        <div className={styles.card}>
          <div className={styles.sectionTitle}>
            <h3>{t('payments.paymentDrawer.proofDocument')}</h3>
          </div>
          {proof.state === 'none' && <span>{t('payments.paymentDrawer.noProof')}</span>}
          {proof.state === 'loading' && <span>{t('payments.paymentDrawer.loadingProof')}</span>}
          {proof.state === 'error' && (
            <span className={styles.error}>
              {t('payments.paymentDrawer.proofError')}
            </span>
          )}
          {proof.state === 'ready' &&
            proof.url &&
            (isImageProof(payment) ? (
              <img className={styles.proofPreview} src={proof.url} alt={t('payments.paymentDrawer.proofAlt')} />
            ) : (
              <a href={proof.url} target="_blank" rel="noopener noreferrer" title={t('payments.paymentDrawer.opensInNewTab')}>
                {t('payments.paymentDrawer.openProof')}
              </a>
            ))}
        </div>
        <div className={styles.card}>
          <div className={styles.sectionTitle}>
            <h3>{t('payments.paymentDrawer.statusHistory')}</h3>
          </div>
          <ol className={styles.history} aria-label={t('payments.paymentDrawer.statusHistoryLabel')}>
            {history.map((item) => (
              <li key={`${item.label}-${item.at}`}>
                <strong>{item.label}</strong>
                <span>
                  {item.actorName ||
                    (item.actorId == null ? 'System' : `Ops user #${item.actorId}`)}{' '}
                  {t('payments.paymentDrawer.historyAt', { date: formatIstDateTime(item.at) })}
                </span>
                {item.reason && <span>{item.reason}</span>}
              </li>
            ))}
          </ol>
        </div>
        <div className={styles.card}>
          <div className={styles.sectionTitle}>
            <h3>{t('payments.paymentDrawer.orderAllocations')}</h3>
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('payments.paymentDrawer.colOrder')}</th>
                  <th>{t('payments.paymentDrawer.colPurpose')}</th>
                  <th>{t('payments.paymentDrawer.colCash')}</th>
                  <th>{t('payments.paymentDrawer.colTds')}</th>
                  <th>{t('payments.paymentDrawer.colStatus')}</th>
                  {manager && <th>{t('payments.paymentDrawer.colAction')}</th>}
                </tr>
              </thead>
              <tbody>
                {payment.allocations.map((allocation) => (
                  <tr key={allocation.id}>
                    <td>
                      <OrderLabel {...allocation} newTab />
                    </td>
                    <td>{label(allocation.purpose)}</td>
                    <td>{formatMoney(parsePaise(allocation.amount))}</td>
                    <td>{formatMoney(parsePaise(allocation.tdsAmount))}</td>
                    <td>
                      <StatusChip
                        displayStatus={
                          allocation.displayStatus ??
                          (payment.status === 'REJECTED'
                            ? 'REJECTED'
                            : allocation.status === 'VOID'
                              ? 'VOID'
                              : payment.status)
                        }
                      />
                    </td>
                    {manager && (
                      <td>
                        {allocation.purpose === 'RECEIPT' &&
                        allocation.status === 'ACTIVE' &&
                        payment.status !== 'VOID' ? (
                          <Button
                            disabled={actionPending}
                            variant="danger" size="sm"
                            aria-label={`Remove allocation #${allocation.shipmentId}`}
                            onClick={() => onVoidAllocation(allocation.id)}
                          >
                            {t('payments.paymentDrawer.removeAllocation')}
                          </Button>
                        ) : (
                          '—'
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {actionError && (
          <div role="alert" className={styles.error}>
            {actionError}
          </div>
        )}
        {!readOnly && onApprove && onReject && (
          <ApproveRejectBar
            paymentId={payment.id}
            status={payment.status}
            role={role}
            onApprove={onApprove}
            onReject={onReject}
            pending={actionPending}
          />
        )}
        {!readOnly && payment.status === 'APPROVED' && onMarkPaid && (
          <Button
            variant="primary" size="sm"
            disabled={actionPending}
            onClick={() => onMarkPaid(payment.id)}
          >
            {t('payments.paymentDrawer.markPaid')}
          </Button>
        )}
        {manager && (payment.status === 'APPROVED' || payment.status === 'PAID') && (
          <Button
            variant="danger" size="sm"
            disabled={actionPending}
            onClick={() => onVoidPayment(payment.id)}
          >
            {payment.direction === 'IN' ? 'Cancel receipt' : 'Cancel payout'}
          </Button>
        )}
      </aside>
    </div>
  );
}
